'use client';

import { useEffect, useRef } from 'react';
import { usePersistedState } from './usePersistedState';
import { gateway } from './api';

export interface ActiveProject {
  slug: string;
  name: string;
  goal: string | null;
  path: string;
  icon?: string | null;
}

/**
 * Global active project state. Persists across navigation and reloads.
 * null = General mode (no project context).
 *
 * On mount, also asks the gateway what project IT thinks is active (set by
 * the last /project/switch). If local state is empty and the gateway has
 * one, we adopt it — so the Telegram Mini App shows whatever project the
 * desktop is currently working on, instead of always defaulting to General.
 */
export function useActiveProject(): [ActiveProject | null, (project: ActiveProject | null) => void] {
  const [project, setProject] = usePersistedState<ActiveProject | null>('active-project', null);
  const synced = useRef(false);

  useEffect(() => {
    if (synced.current) return;
    synced.current = true;
    if (project) return;  // local already has one — don't clobber
    // `project` hydrates async, so it's still null on first mount even when
    // localStorage HAS a saved project — check storage synchronously, or the
    // gateway's stale pointer clobbers the user's pick on every navigation
    // (the "chat keeps switching back to General" bug).
    try {
      const stored = localStorage.getItem('beast:active-project');
      if (stored && stored !== 'null') return;
    } catch { /* storage unavailable — fall through to gateway sync */ }
    gateway.session()
      .then((s: any) => {
        const p = s?.project;
        if (p && p.slug && p.name && p.path) {
          setProject({
            slug: p.slug,
            name: p.name,
            goal: null,
            path: p.path,
            icon: p.icon ?? null,
          });
        }
      })
      .catch(() => {});
  }, [project, setProject]);

  return [project, setProject];
}
